import type { Session } from "@supabase/supabase-js";
import { useCallback, useEffect, useState } from "react";
import { adminRequest, type WorkflowRow } from "../adminApi";
import { LoadingPanel } from "../components/LoadingPanel";
import { Pagination } from "../components/Pagination";

const PAGE_SIZE = 50;

const TRIGGER_OPTIONS = ["all", "manual", "scheduled"];

export function WorkflowsPage({
  mfaToken,
  session,
  setNotice
}: {
  mfaToken: string | null;
  session: Session;
  setNotice(n: { tone: "error" | "success"; text: string } | null): void;
}) {
  const [workflows, setWorkflows] = useState<WorkflowRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [enabledFilter, setEnabledFilter] = useState("all");
  const [triggerFilter, setTriggerFilter] = useState("all");

  const loadPage = useCallback(async (p: number) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("offset", String((p - 1) * PAGE_SIZE));
      params.set("limit", String(PAGE_SIZE));
      if (enabledFilter !== "all") params.set("enabled", enabledFilter);
      if (triggerFilter !== "all") params.set("trigger_type", triggerFilter);

      const data = await adminRequest<{ workflows: WorkflowRow[]; total_count: number }>(
        `/workflows?${params}`,
        { mfaToken, session }
      );
      setWorkflows(data.workflows);
      setTotalCount(data.total_count);
      setPage(p);
    } catch (caught) {
      setNotice({ tone: "error", text: caught instanceof Error ? caught.message : "Could not load workflows." });
    } finally {
      setLoading(false);
    }
  }, [mfaToken, session, setNotice, enabledFilter, triggerFilter]);

  useEffect(() => { void loadPage(1); }, [loadPage]);

  return (
    <div className="stack">
      <section className="section">
        <div className="sectionHead">
          <div>
            <h1>Workflows</h1>
            <p>All workflows across every user, filterable by status and trigger type.</p>
          </div>
          <button onClick={() => void loadPage(page)} type="button">Refresh</button>
        </div>

        <div className="filterBar">
          <select value={enabledFilter} onChange={(e) => { setEnabledFilter(e.target.value); setPage(1); }}>
            <option value="all">All states</option>
            <option value="true">Enabled</option>
            <option value="false">Disabled</option>
          </select>
          <select value={triggerFilter} onChange={(e) => { setTriggerFilter(e.target.value); setPage(1); }}>
            {TRIGGER_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt === "all" ? "All triggers" : opt}</option>
            ))}
          </select>
        </div>

        {loading ? <LoadingPanel label="Loading workflows…" /> : <WorkflowsTable workflows={workflows} />}
        <Pagination currentPage={page} pageSize={PAGE_SIZE} totalCount={totalCount} onPageChange={loadPage} />
      </section>
    </div>
  );
}

function WorkflowsTable({ workflows }: { workflows: WorkflowRow[] }) {
  return (
    <div className="tableWrap">
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>User</th>
            <th>Steps</th>
            <th>Trigger</th>
            <th>Status</th>
            <th>Runs</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {workflows.map((wf) => (
            <tr key={wf.id}>
              <td><strong>{wf.name}</strong></td>
              <td>{wf.user_email ?? "—"}</td>
              <td>{wf.step_count}</td>
              <td><span className={`tag tag--${wf.trigger_type}`}>{wf.trigger_type}</span></td>
              <td>
                <span className={`tag tag--${wf.enabled ? "enabled" : "disabled"}`}>
                  {wf.enabled ? "Enabled" : "Disabled"}
                </span>
              </td>
              <td>{wf.run_count}</td>
              <td>{formatDate(wf.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!workflows.length ? <div className="empty">No workflows match the current filters.</div> : null}
    </div>
  );
}

function formatDate(value: string) {
  return new Date(value).toLocaleString();
}
